import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router';
import { AppLayout, ScreenSkeleton } from './layout.js';
import { LoginScreen } from '../routes/login.js';
import { DashboardScreen } from '../routes/dashboard.js';
import { useSession } from '../data/session.js';

/**
 * Routes from PLAN §13.2.
 *
 * The dashboard is bundled with the shell because it is what opens on launch.
 * Everything else is lazy, which is what keeps the initial download inside the
 * 200 KB gzipped budget (PLAN §17.4) — the composer in particular pulls in the
 * uploader and would otherwise be paid for on every cold start.
 */
const PostsScreen = lazy(() =>
  import('../routes/posts.js').then((m) => ({ default: m.PostsScreen })),
);
const PostDetailScreen = lazy(() =>
  import('../routes/post-detail.js').then((m) => ({ default: m.PostDetailScreen })),
);
const ComposerScreen = lazy(() =>
  import('../routes/composer/composer.js').then((m) => ({ default: m.ComposerScreen })),
);
const CalendarScreen = lazy(() =>
  import('../routes/calendar.js').then((m) => ({ default: m.CalendarScreen })),
);
const AnalyticsScreen = lazy(() =>
  import('../routes/analytics.js').then((m) => ({ default: m.AnalyticsScreen })),
);
const MediaScreen = lazy(() =>
  import('../routes/media.js').then((m) => ({ default: m.MediaScreen })),
);
const AccountsScreen = lazy(() =>
  import('../routes/accounts.js').then((m) => ({ default: m.AccountsScreen })),
);
const SettingsScreen = lazy(() =>
  import('../routes/settings.js').then((m) => ({ default: m.SettingsScreen })),
);
const ShareScreen = lazy(() =>
  import('../routes/share.js').then((m) => ({ default: m.ShareScreen })),
);

/**
 * The primitives reference (`/ui-preview`), mounted in development only.
 *
 * It renders every control in the states that actually occur and touches no
 * API, so it is the one screen that can be opened without PostgreSQL, Redis or
 * a session — which is the whole point: a design change should be verifiable
 * in a browser without standing the backend up first.
 *
 * It sat in the tree for weeks with no route to it, so the reference its own
 * doc comment tells you to reproduce bugs in could not be opened at all.
 *
 * The `lazy()` call lives *inside* the branch on purpose. With it hoisted to
 * module scope the route entry disappeared from a production build but the
 * chunk still shipped, because the dynamic import was still referenced. Here
 * `import.meta.env.DEV` is statically false, the whole array literal folds to
 * `[]`, and nothing refers to the import any more — so Rollup drops the chunk.
 * Verified by checking `dist/assets` for it, not by assuming.
 */
const devOnlyRoutes = import.meta.env.DEV
  ? [
      {
        path: '/ui-preview',
        element: (() => {
          const UiPreview = lazy(() =>
            import('../routes/ui-preview.js').then((m) => ({ default: m.UiPreview })),
          );
          return (
            <Suspense fallback={<ScreenSkeleton />}>
              <UiPreview />
            </Suspense>
          );
        })(),
      },
    ]
  : [];

/**
 * The gate. While the session is still loading nothing is decided — rendering
 * a redirect too early would bounce a signed-in owner to /login on every cold
 * start.
 */
function RequireSession() {
  const session = useSession();
  const location = useLocation();

  // Full height: this renders before AppLayout exists, so there is nothing
  // else on the page to centre against.
  if (session.isPending) return <ScreenSkeleton full />;
  if (!session.data) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginScreen /> },
  ...devOnlyRoutes,
  {
    element: <RequireSession />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <DashboardScreen /> },
          { path: '/posts', element: <PostsScreen /> },
          { path: '/posts/:id', element: <PostDetailScreen /> },
          { path: '/posts/:id/edit', element: <ComposerScreen /> },
          { path: '/compose', element: <ComposerScreen /> },
          { path: '/calendar', element: <CalendarScreen /> },
          { path: '/analytics', element: <AnalyticsScreen /> },
          { path: '/media', element: <MediaScreen /> },
          { path: '/accounts', element: <AccountsScreen /> },
          { path: '/settings', element: <SettingsScreen /> },
          { path: '/share', element: <ShareScreen /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);

function NotFound() {
  return (
    <div className="p-6">
      <h1 className="text-lg font-semibold text-ink">No such screen</h1>
      <p className="mt-1 text-sm text-muted">The link may be from an older version of the app.</p>
    </div>
  );
}
